import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Table,
  Badge,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  getFirestore,
} from "firebase/firestore";
import {
  FaFileAlt,
  FaPrint,
  FaSearch,
  FaDownload,
  FaUsers,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaFilter,
  FaCog,
} from "react-icons/fa";
import { BsFileEarmarkPdf, BsDownload } from "react-icons/bs";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import qrcode from "qrcode";

import JSZip from "jszip"; // Importar JSZip
import { saveAs } from "file-saver"; // Importar FileSaver
import { procesarDocumentoIndividual } from "./utils/bulkDocumentSender";

const db = getFirestore(firebaseApp);

// Utilidad para convertir imagen remota a base64 y convertir a JPEG (sin prefijo)
const getBase64FromUrl = async (url) => {
  console.log("Cargando imagen desde URL:", url); // Debug

  try {
    // Agregar headers para manejar CORS si es necesario
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    console.log("Blob obtenido:", blob.type, blob.size); // Debug

    // Convertir a base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const fullBase64 = reader.result;
        const base64Data = fullBase64.split(",")[1]; // Extraer solo la parte base64 (sin prefijo)
        console.log("Base64 generado exitosamente"); // Debug
        resolve(base64Data);
      };
      reader.onerror = (error) => {
        console.error("Error en FileReader:", error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });

    // Crear un canvas para convertir a JPEG (jsPDF maneja mejor JPEG)
    const img = new Image();
    img.crossOrigin = "anonymous";

    const jpegBase64 = await new Promise((resolve, reject) => {
      img.onload = () => {
        console.log("Imagen cargada en canvas, convirtiendo a JPEG"); // Debug

        // Crear canvas
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Mantener proporciones
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        // Dibujar imagen (maneja transparencia convirtiéndola a blanco)
        ctx.fillStyle = "#FFFFFF"; // Fondo blanco para transparencia
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        // Convertir a JPEG y extraer solo el base64 (sin prefijo)
        const fullJpegBase64 = canvas.toDataURL("image/jpeg", 0.9);
        const jpegBase64Data = fullJpegBase64.split(",")[1]; // Extraer solo la parte base64
        console.log("JPEG generado exitosamente"); // Debug
        resolve(jpegBase64Data);
      };
      img.onerror = (error) => {
        console.error("Error cargando imagen en canvas:", error);
        reject(error);
      };
      img.src = `data:image/png;base64,${base64}`; // Usar el base64 original para cargar en img
    });

    // Obtener dimensiones finales
    const tempImg = new Image();
    const dimensions = await new Promise((resolve, reject) => {
      tempImg.onload = () => {
        console.log(
          "Dimensiones finales:",
          tempImg.naturalWidth,
          tempImg.naturalHeight
        ); // Debug
        resolve({
          width: tempImg.naturalWidth,
          height: tempImg.naturalHeight,
        });
      };
      tempImg.onerror = reject;
      tempImg.src = `data:image/jpeg;base64,${jpegBase64}`; // Usar el base64 JPEG para obtener dimensiones
    });

    return {
      base64: jpegBase64, // Base64 puro sin prefijo
      width: dimensions.width,
      height: dimensions.height,
    };
  } catch (error) {
    console.error("Error en getBase64FromUrl:", error);
    throw error;
  }
};

// Utilidad para calcular horas entre dos horas tipo "HH:mm"
const calcularHorasEntre = (horaInicio, horaFin) => {
  if (!horaInicio || !horaFin) return 0;
  const [h1, m1] = horaInicio.split(":").map(Number);
  const [h2, m2] = horaFin.split(":").map(Number);
  let minutos = h2 * 60 + m2 - (h1 * 60 + m1);
  if (minutos < 0) minutos += 24 * 60;
  return minutos / 60;
};

// Calcular asistencia suponiendo que no faltó ningún día
const calcularAsistencia = (regimen, diasMes = 30) => {
  if (!regimen)
    return { dias_trabajados: 0, horas_trabajadas: 0, dias_descanso: 0 };

  const horasJornada = calcularHorasEntre(
    regimen.hora_inicio,
    regimen.hora_fin
  );
  const horasRefrigerio = calcularHorasEntre(
    regimen.hora_refrigerio_inicio,
    regimen.hora_refrigerio_fin
  );
  const horasPorDia = Math.max(0, horasJornada - horasRefrigerio);

  let dias_trabajados = 0;
  let horas_trabajadas = 0;
  let dias_descanso = 0;

  if (regimen.dias_trabajo > 0 && regimen.dias_descanso > 0) {
    // Turnos tipo 21x10, 14x7, etc.
    const ciclo = regimen.dias_trabajo + regimen.dias_descanso;
    const ciclosCompletos = Math.floor(diasMes / ciclo);
    const diasRestantes = diasMes % ciclo;
    dias_trabajados =
      ciclosCompletos * regimen.dias_trabajo +
      Math.min(diasRestantes, regimen.dias_trabajo);
    dias_descanso =
      ciclosCompletos * regimen.dias_descanso +
      Math.max(0, diasRestantes - regimen.dias_trabajo);
    horas_trabajadas = dias_trabajados * horasPorDia;
  } else {
    // Turnos semanales (ej: lunes a viernes)
    const diasLaborables = regimen.dias_laborables?.length || 0;
    dias_trabajados = Math.round(diasLaborables * (diasMes / 7));
    dias_descanso = diasMes - dias_trabajados;
    horas_trabajadas = dias_trabajados * horasPorDia;
  }

  return {
    dias_trabajados,
    horas_trabajadas: parseFloat(horas_trabajadas.toFixed(2)),
    dias_descanso,
  };
};

// Devuelve los datos de asistencia en formato clave-valor para la tabla de 4 columnas
const getAsistenciaDetalle = (boleta) => {
  return [
    { label: "Días Trabajados", value: boleta.dias_trabajados ?? 0 },
    { label: "Días Descanso", value: boleta.dias_descanso ?? 0 },
    { label: "Descanso Médico", value: boleta.dias_descanso_medico ?? 0 },
    { label: "Inasistencia", value: boleta.inasistencia ?? 0 },
    { label: "Lic. con Goce", value: boleta.lic_con_goce ?? 0 },
    { label: "Lic. sin Goce", value: boleta.lic_sin_goce ?? 0 },
    { label: "Vacaciones", value: boleta.dias_vacaciones ?? boleta.vacaciones_dias ?? 0 },
    { label: "H.E. 25%", value: boleta.he_25 ?? 0 },
    { label: "H.E. 35%", value: boleta.he_35 ?? 0 },
    { label: "Feriados 100%", value: boleta.he_100 ?? boleta.feriados_100 ?? 0 },
  ];
};

const BoletaPago = ({ usuario }) => {
  const [empleados, setEmpleados] = useState([]);
  const [boletas, setBoletas] = useState([]);
  const [company, setCompany] = useState(null);
  const [filtros, setFiltros] = useState({
    empleado: "",
    mes: new Date().getMonth() + 1,
    anio: new Date().getFullYear(),
  });
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pId = params.get("planillaId");
    if (pId) {
      const parts = pId.split("_");
      if (parts.length >= 2) {
        const [anio, mes] = parts[1].split("-");
        if (anio && mes) {
          setFiltros((prev) => ({ ...prev, anio: parseInt(anio, 10), mes: parseInt(mes, 10) }));
        }
      }
    }
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      // Cargar datos de la empresa
      const companyRef = doc(db, "company", usuario.ruc);
      const companySnap = await getDoc(companyRef);
      if (companySnap.exists()) {
        setCompany(companySnap.data());
      }

      // Cargar empleados
      const empleadosRef = collection(db, "pl_empleados");
      const q = query(empleadosRef, where("ruc_empresa", "==", usuario.ruc));
      const querySnapshot = await getDocs(q);
      const empleadosData = [];
      querySnapshot.forEach((doc) => {
        empleadosData.push({ id: doc.id, ...doc.data() });
      });
      setEmpleados(empleadosData);

      // Cargar configuración de planilla (para SIS vs ESSALUD)
      const configRef = doc(db, "pl_config", usuario.ruc);
      const configSnap = await getDoc(configRef);
      if (configSnap.exists()) {
        setConfig(configSnap.data());
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      mostrarError("Error al cargar los datos necesarios");
    } finally {
      setLoading(false);
    }
  };

  const buscarBoletas = async () => {
    setSearchLoading(true);
    try {
      const detallesRef = collection(db, "pl_planilla_detalle");
      let periodo = `${filtros.anio}-${filtros.mes
        .toString()
        .padStart(2, "0")}`;
      let q = query(
        detallesRef,
        where("ruc_empresa", "==", usuario.ruc),
        where("periodo", "==", periodo)
      );

      if (filtros.empleado) {
        q = query(q, where("empleado_id", "==", filtros.empleado));
      }

      const querySnapshot = await getDocs(q);
      const boletasData = [];
      for (const docSnap of querySnapshot.docs) {
        const boleta = { id: docSnap.id, ...docSnap.data() };

        // Buscar empleado y régimen
        const empleado = empleados.find((e) => e.id === boleta.empleado_id);
        let regimen = null;
        if (empleado?.regimen_trabajo_id) {
          const regimenSnap = await getDoc(
            doc(db, "pl_regimen_trabajo", empleado.regimen_trabajo_id)
          );
          if (regimenSnap.exists()) regimen = regimenSnap.data();
        }

        // Calcular asistencia SIEMPRE si hay régimen
        if (regimen) {
          const diasMes = new Date(filtros.anio, filtros.mes, 0).getDate();
          const asistencia = calcularAsistencia(regimen, diasMes);
          // Solo asignar si no vienen ya de la planilla (para no sobrescribir datos reales de la planilla)
          if (boleta.dias_trabajados === undefined) boleta.dias_trabajados = asistencia.dias_trabajados;
          if (boleta.horas_trabajadas === undefined) boleta.horas_trabajadas = asistencia.horas_trabajadas;
          if (boleta.dias_descanso === undefined) boleta.dias_descanso = asistencia.dias_descanso;
        }

        // Mapeo de inasistencias desde planilla
        if (boleta.faltas_injustificadas !== undefined) {
          boleta.inasistencia = boleta.faltas_injustificadas;
        }
        if (boleta.descuentos?.descuento_faltas !== undefined) {
          if (!boleta.descuentos) boleta.descuentos = {};
          boleta.descuentos.inasistencia = boleta.descuentos.descuento_faltas;
        }

        // Mapeo robusto de vacaciones (dias)
        if (boleta.dias_vacaciones === undefined && boleta.vacaciones_dias !== undefined) {
          boleta.dias_vacaciones = boleta.vacaciones_dias;
        }
        // Solo intentar calcular si realmente no tenemos el campo de dias
        if (boleta.dias_vacaciones === undefined && boleta.vacaciones > 0 && boleta.sueldo_base > 0) {
          // Fallback: calcular dias si tenemos el monto en soles
          // Usamos sueldo_base_original si está disponible o sueldo_base
          const sueldoRef = boleta.sueldo_base_original || (boleta.sueldo_base + (boleta.vacaciones || 0));
          boleta.dias_vacaciones = Number((boleta.vacaciones / (sueldoRef / 30)).toFixed(0));
        }

        // Normalización de Horas Extras
        if (boleta.he_100 === undefined && boleta.feriados_100 !== undefined) {
          boleta.he_100 = boleta.feriados_100;
        }

        // Los totales ya vienen calculados desde la base de datos, solo los asignamos
        // Si no existen, los calculamos como fallback
        if (!boleta.total_ingresos_bruto) {
          let totalBonos = 0;
          if (boleta.bonos && typeof boleta.bonos === 'object') {
            totalBonos = Object.values(boleta.bonos).reduce((sum, monto) => sum + (Number(monto) || 0), 0);
          }

          boleta.total_ingresos_bruto =
            (boleta.sueldo_base || 0) +
            (boleta.asignacion_familiar || 0) +
            totalBonos +
            (boleta.horas_extras || 0) +
            (boleta.ingresos?.horas_extras_25 || 0) +
            (boleta.ingresos?.horas_extras_35 || 0) +
            (boleta.ingresos?.horas_extras_100 || 0) +
            (boleta.bonificacion_extraordinaria || 0) +
            (boleta.comisiones || 0) +
            (boleta.gratificacion || 0) +
            (boleta.vacaciones_truncas || 0) +
            (boleta.beneficios_sociales || 0) +
            (boleta.vacaciones || 0) +
            (boleta.cts || 0) +
            (boleta.otros_ingresos || 0);
        }

        // Usar total_ingresos_bruto como total de ingresos
        boleta.total_ingresos = boleta.total_ingresos_bruto;

        if (!boleta.total_descuentos) {
          boleta.total_descuentos =
            (boleta.sistema_pension?.total || 0) +
            (boleta.descuentos?.renta_5ta || 0) +
            (boleta.descuentos?.inasistencia || 0) +
            (boleta.descuentos?.tarjeta_visa || 0) +
            (boleta.descuentos?.pension_alimenticia || 0) +
            (boleta.descuentos?.pago_utilidades || 0) +
            (boleta.descuentos?.prestamos || 0) +
            (boleta.otros_descuentos || 0) +
            (boleta.descuentos?.pago_bono || 0) +
            (boleta.descuentos?.liquidaciones || 0);
        }

        if (!boleta.total_aportaciones) {
          boleta.total_aportaciones = boleta.aportaciones?.total || 0;
        }

        if (!boleta.neto_pagar) {
          boleta.neto_pagar = boleta.total_ingresos_bruto - boleta.total_descuentos;
        }

        boletasData.push(boleta);
      }
      setBoletas(boletasData);
    } catch (error) {
      console.error("Error al buscar boletas:", error);
      mostrarError("Error al buscar las boletas");
    } finally {
      setSearchLoading(false);
    }
  };

  // Utilidad para obtener mes en texto
  const getMesTexto = (mes) => {
    return new Date(2000, mes - 1)
      .toLocaleString("es", { month: "long" })
      .toUpperCase();
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "";

    let dateObj;
    // Si es Timestamp de Firestore
    if (fecha && fecha.seconds) {
      dateObj = new Date(fecha.seconds * 1000);
    }
    // Si es string YYYY-MM-DD
    else if (typeof fecha === 'string' && fecha.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [y, m, d] = fecha.split('-').map(Number);
      dateObj = new Date(y, m - 1, d);
    }
    // Si es objeto Date
    else if (fecha instanceof Date) {
      dateObj = fecha;
    }
    // Fallback para otros formatos
    else {
      dateObj = new Date(fecha);
    }

    if (isNaN(dateObj.getTime())) return fecha; // Retornar original si falla

    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const generarPDF = async (boleta, autoGuardar = true) => {
    const docPDF = new jsPDF();

    // Generar QR para verificación de autenticidad
    let qrBase64 = null;
    try {
      // Datos del QR: enlace a página de verificación con ID único de la boleta
      const qrData = `https://www.miboleta.net.pe/verificar?id=${boleta.id}&periodo=${boleta.periodo}`;
      qrBase64 = await qrcode.toDataURL(qrData, { width: 100, margin: 1 }); // Generar QR como base64, tamaño 100x100
    } catch (error) {
      console.error("Error generando QR:", error);
    }

    // Usar los datos de la empresa ya cargados desde el estado
    const empresaDatos = company;

    let logoWidth = 0;
    let logoHeight = 0;

    // Agregar logo si existe, alineado a la izquierda
    if (empresaDatos?.logo) {
      try {
        const {
          base64: logoBase64,
          width: naturalWidth,
          height: naturalHeight,
        } = await getBase64FromUrl(empresaDatos.logo);

        // Calcular proporción para mantener aspect ratio con mejor tamaño
        const aspectRatio = naturalWidth / naturalHeight;
        logoWidth = 45; // Ancho óptimo
        logoHeight = logoWidth / aspectRatio;

        // Si el alto supera el límite, ajustar basado en alto
        if (logoHeight > 35) {
          logoHeight = 35;
          logoWidth = logoHeight * aspectRatio;
        }

        // Posición alineada a la izquierda
        docPDF.addImage(logoBase64, "JPEG", 10, 8, logoWidth, logoHeight);
      } catch (error) {
        console.error("Error al cargar logo:", error);
      }
    }

    // Título "BOLETA DE PAGO" alineado al centro
    docPDF.setFont("helvetica", "bold");
    docPDF.setFontSize(16);
    const pageWidth = docPDF.internal.pageSize.width;
    const titleWidth = docPDF.getTextWidth("BOLETA DE PAGO");
    const titleX = (pageWidth - titleWidth) / 2; // Centro
    docPDF.text("BOLETA DE PAGO", titleX, 15);

    // Mes - alineado al centro
    docPDF.setFont("helvetica", "normal");
    docPDF.setFontSize(11);
    const mesTexto = `MES DE ${getMesTexto(
      parseInt(boleta.periodo.split("-")[1])
    )} ${boleta.periodo.split("-")[0]}`;
    const mesWidth = docPDF.getTextWidth(mesTexto);
    const mesX = (pageWidth - mesWidth) / 2; // Centro
    docPDF.text(mesTexto, mesX, 25);

    // QR alineado a la derecha, si existe
    if (qrBase64) {
      const qrSize = 25; // Tamaño del QR
      const qrX = pageWidth - qrSize - 10; // Alineado a la derecha con margen
      docPDF.addImage(qrBase64, "PNG", qrX, 8, qrSize, qrSize);
    }

    // Línea decorativa sutil debajo del header
    docPDF.setDrawColor(139, 69, 19); // Color marrón similar al tema
    docPDF.setLineWidth(0.5);
    docPDF.line(10, 35, 200, 35);

    // Buscar datos del empleado
    const empleado = empleados.find((e) => e.id === boleta.empleado_id);

    // Buscar datos adicionales del empleado por DNI
    let empleadoCompleto = empleado;
    if (boleta.empleado_id) {
      try {
        const empleadoRef = doc(db, "pl_empleados", boleta.empleado_id);
        const empleadoSnap = await getDoc(empleadoRef);
        if (empleadoSnap.exists()) {
          empleadoCompleto = { ...empleado, ...empleadoSnap.data() };
        }
      } catch (error) {
        console.error("Error al buscar datos del empleado:", error);
      }
    }

    // Calcular días del mes y asistencia (lunes a viernes)
    const [anio, mes] = boleta.periodo.split("-");
    const diasMes = new Date(parseInt(anio), parseInt(mes), 0).getDate();
    let diasTrabajados = 0;
    let diasDescanso = 0;

    // Si la boleta ya tiene los días calculados desde la planilla, usarlos
    if (boleta.dias_trabajados !== undefined || boleta.dias_descanso !== undefined) {
      diasTrabajados = boleta.dias_trabajados ?? 0;
      diasDescanso = boleta.dias_descanso ?? 0;
    } else {
      // Fallback: Contar días laborables (lunes a viernes) del mes
      for (let dia = 1; dia <= diasMes; dia++) {
        const fecha = new Date(parseInt(anio), parseInt(mes) - 1, dia);
        const diaSemana = fecha.getDay(); // 0 = domingo, 6 = sábado
        if (diaSemana >= 1 && diaSemana <= 5) {
          // lunes a viernes
          diasTrabajados++;
        } else {
          diasDescanso++;
        }
      }
      // Restar inasistencias si existen en el fallback
      diasTrabajados = Math.max(0, diasTrabajados - (boleta.inasistencia || 0));
    }

    // Tabla de datos del empleador
    const datosEmpleador = [
      [
        empresaDatos?.ruc || "",
        empresaDatos?.name || "",
        empresaDatos?.direccion || "",
      ],
    ];

    autoTable(docPDF, {
      startY: 40,
      head: [["DATOS DEL EMPLEADOR", "", ""]],
      body: [["RUC", "RAZÓN SOCIAL", "DIRECCIÓN"], ...datosEmpleador],
      theme: "striped",
      styles: {
        fontSize: 7,
        halign: "left",
      },
      headStyles: {
        fillColor: [139, 69, 19], // Color marrón
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      columnStyles: {
        1: { halign: "left" },
        2: { halign: "left" },
      },
    });

    // Tabla de datos del empleado
    const datosEmpleado = [
      [
        "APELLIDOS Y NOMBRES",
        `${empleadoCompleto?.apellidos || ""} ${empleadoCompleto?.nombres || boleta.nombres || ""
        }`,
        "DNI/PTP/CE N°",
        boleta.empleado_id || "",
      ],
      [
        "FECHA DE INGRESO",
        formatearFecha(empleadoCompleto?.fecha_ingreso) || "",
        "OCUPACIÓN",
        empleadoCompleto?.cargo || "",
      ],
      [
        "PERSONAL DE CONFIANZA",
        empleadoCompleto?.personal_confianza ? "SÍ" : "NO",
        (boleta.regimen_aplicado === "MYPE_MICRO" && config?.tipo_seguro_micro === "SIS") ? "CODIGO SIS" : "CODIGO ESSALUD",
        empleadoCompleto?.codigo_essalud || "",
      ],
      [
        "SISTEMA DE PENSIONES",
        `${boleta.regimen_pensionario || ""} ${boleta.descuentos?.sistema_pension?.nombre_afp || ""
        }`,
        "CUSPP",
        empleadoCompleto?.cuspp ||
        boleta.descuentos?.sistema_pension?.cuspp ||
        "",
      ],
      [
        "BANCO - CUENTA",
        `${empleadoCompleto?.banco || ""} - ${empleadoCompleto?.cuenta_bancaria || ""
        }`,
        "CENTRO DE COSTO",
        empleadoCompleto?.centro_costo || "",
      ],
    ];

    autoTable(docPDF, {
      startY: docPDF.lastAutoTable.finalY,
      head: [["DATOS DEL EMPLEADO", "", "", ""]],
      body: datosEmpleado,
      theme: "striped",
      styles: {
        fontSize: 7,
        halign: "left",
      },
      headStyles: {
        fillColor: [139, 69, 19], // Color marrón
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      columnStyles: {
        1: { halign: "left" },
        3: { halign: "left" },
      },
    });

    // Tabla de detalle de asistencia en 8 columnas
    const detalleAsistencia = [
      [
        "DÍAS TRABAJADOS",
        diasTrabajados,
        "DÍAS DESCANSO",
        diasDescanso,
        "DESCANSO MÉDICO",
        boleta.dias_descanso_medico || 0,
        "INASISTENCIA",
        boleta.inasistencia || 0,
      ],
      [
        "LIC. CON GOCE",
        boleta.lic_con_goce || 0,
        "LIC. SIN GOCE",
        boleta.lic_sin_goce || 0,
        "VACACIONES",
        boleta.dias_vacaciones || boleta.vacaciones_dias || 0,
        "H.E. 25%",
        boleta.he_25 || 0,
      ],
      [
        "H.E. 35%",
        boleta.he_35 || 0,
        "FERIADOS 100%",
        boleta.he_100 || boleta.feriados_100 || 0,
        "",
        "",
        "",
        "",
      ],
    ];

    autoTable(docPDF, {
      startY: docPDF.lastAutoTable.finalY,
      head: [["DETALLE DE ASISTENCIA", "", "", "", "", "", "", ""]],
      body: detalleAsistencia,
      theme: "striped",
      styles: {
        fontSize: 7,
        halign: "left",
      },
      headStyles: {
        fillColor: [139, 69, 19], // Color marrón
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      columnStyles: {
        0: { halign: "left" }, // Etiquetas columna 1
        1: { halign: "center" }, // Valores columna 1
        2: { halign: "left" }, // Etiquetas columna 2
        3: { halign: "center" }, // Valores columna 2
        4: { halign: "left" }, // Etiquetas columna 3
        5: { halign: "center" }, // Valores columna 3
        6: { halign: "left" }, // Etiquetas columna 4
        7: { halign: "center" }, // Valores columna 4
      },
    });

    // Tabla de 6 columnas: Ingresos - Valor - Descuentos - Valor - Aportaciones - Valor
    const tableData = [];

    // Obtener el mes del período para determinar qué conceptos mostrar
    const mesPeriodo = parseInt(boleta.periodo.split('-')[1], 10);

    // Preparar datos de ingresos
    const ingresos = [];

    // Agregar ingresos fijos
    ingresos.push(["SUELDO BASE", `S/ ${(boleta.sueldo_base || 0).toFixed(2)}`]);
    ingresos.push(["ASIGNACIÓN FAMILIAR", `S/ ${(boleta.asignacion_familiar || 0).toFixed(2)}`]);

    // Agregar bonos individuales si existen
    if (boleta.bonos && typeof boleta.bonos === 'object') {
      Object.entries(boleta.bonos).forEach(([nombreBono, monto]) => {
        if (monto && monto > 0) {
          // Truncar nombre del bono a 25 caracteres máximo
          const nombreTruncado = nombreBono.length > 25
            ? nombreBono.substring(0, 25) + "..."
            : nombreBono;
          ingresos.push([nombreTruncado.toUpperCase(), `S/ ${Number(monto).toFixed(2)}`]);
        }
      });
    }

    // Agregar otros ingresos fijos
    // Gratificación y Bonificación Extraordinaria solo en Julio (7) y Diciembre (12)
    if (mesPeriodo === 7 || mesPeriodo === 12) {
      ingresos.push(["BONIFICACIÓN EXTRAORDINARIA", `S/ ${(boleta.bonificacion_extraordinaria || 0).toFixed(2)}`]);
      ingresos.push(["GRATIFICACIÓN", `S/ ${(boleta.gratificacion || 0).toFixed(2)}`]);
    }

    // CTS solo en Mayo (5) y Noviembre (11)
    if (mesPeriodo === 5 || mesPeriodo === 11) {
      ingresos.push(["CTS", `S/ ${(boleta.cts || 0).toFixed(2)}`]);
    }

    ingresos.push(["VACACIONES", `S/ ${(boleta.vacaciones || 0).toFixed(2)}`]);

    if (boleta.ingresos?.horas_extras_25 > 0) {
      ingresos.push(["H.E. 25%", `S/ ${boleta.ingresos.horas_extras_25.toFixed(2)}`]);
    }
    if (boleta.ingresos?.horas_extras_35 > 0) {
      ingresos.push(["H.E. 35%", `S/ ${boleta.ingresos.horas_extras_35.toFixed(2)}`]);
    }
    if (boleta.ingresos?.horas_extras_100 > 0) {
      ingresos.push(["H.E. 100%", `S/ ${boleta.ingresos.horas_extras_100.toFixed(2)}`]);
    }

    // Preparar datos de descuentos (Dinámico para transparencia)
    const descuentos = [];

    if (boleta.descuentos?.sistema_pension?.onp > 0) {
      descuentos.push(["ONP", `S/ ${boleta.descuentos.sistema_pension.onp.toFixed(2)}`]);
    } else if (boleta.descuentos?.sistema_pension?.total > 0) {
      const sp = boleta.descuentos.sistema_pension;
      if (sp.fondo > 0) descuentos.push(["FONDO PENSIONES", `S/ ${sp.fondo.toFixed(2)}`]);
      if (sp.comision > 0) descuentos.push(["COMISIÓN AFP", `S/ ${sp.comision.toFixed(2)}`]);
      if (sp.seguro > 0) descuentos.push(["SEGURO AFP", `S/ ${sp.seguro.toFixed(2)}`]);
    }

    if (boleta.descuentos?.renta_5ta > 0) {
      descuentos.push(["RENTA 5TA CATEGORÍA", `S/ ${boleta.descuentos.renta_5ta.toFixed(2)}`]);
    }

    if (boleta.descuentos?.inasistencia > 0) {
      descuentos.push(["INASISTENCIAS", `S/ ${boleta.descuentos.inasistencia.toFixed(2)}`]);
    } else if (boleta.descuentos?.descuento_faltas > 0) {
      descuentos.push(["INASISTENCIAS", `S/ ${boleta.descuentos.descuento_faltas.toFixed(2)}`]);
    }

    if (boleta.descuentos?.descuento_dso > 0) {
      descuentos.push(["DSO (DESC. SEMANAL)", `S/ ${boleta.descuentos.descuento_dso.toFixed(2)}`]);
    }

    if (boleta.descuentos?.adelantos > 0) {
      descuentos.push(["ADELANTO DE SALARIO", `S/ ${boleta.descuentos.adelantos.toFixed(2)}`]);
    }

    // Otros descuentos que podrían venir de la planilla
    const otrosPosibles = [
      { key: 'prestamos', label: 'PRÉSTAMOS' },
      { key: 'pension_alimenticia', label: 'PENSIÓN ALIMENTICIA' },
      { key: 'tarjeta_visa', label: 'DSCTO. TARJETA' },
      { key: 'otros', label: 'OTROS DESCUENTOS' }
    ];

    otrosPosibles.forEach(desc => {
      if (boleta.descuentos?.[desc.key] > 0) {
        descuentos.push([desc.label, `S/ ${Number(boleta.descuentos[desc.key]).toFixed(2)}`]);
      }
    });

    // Asegurar que si hay un total de descuentos pero la lista es corta, se explique la diferencia
    const sumaVisibleDescuentos = descuentos.reduce((sum, d) => sum + (parseFloat(d[1].replace('S/ ', '')) || 0), 0);
    const diferencia = (boleta.total_descuentos || 0) - sumaVisibleDescuentos;
    if (diferencia > 0.01) {
      descuentos.push(["OTROS CARGOS", `S/ ${diferencia.toFixed(2)}`]);
    }

    // Preparar datos de aportaciones
    let saludLabel = "ESSALUD (9%)";
    const esSIS = (boleta.regimen_aplicado === "MYPE_MICRO" && config?.tipo_seguro_micro === "SIS") || 
                  (boleta.aportaciones?.essalud > 0 && Math.abs(boleta.aportaciones?.essalud - (config?.costo_sis || 15)) < 0.01);
    
    if (esSIS) {
      saludLabel = "SIS";
    }

    const aportaciones = [
      [saludLabel, `S/ ${(boleta.aportaciones?.essalud || 0).toFixed(2)}`],
      ["ESSALUD VIDA", `S/ ${(boleta.aportaciones?.essalud_vida || boleta.descuentos?.essalud_vida || 0).toFixed(2)}`],
      ["SCTR", `S/ ${(boleta.aportaciones?.sctr || 0).toFixed(2)}`],
      ["VIDA LEY", `S/ ${(boleta.aportaciones?.vida_ley || 0).toFixed(2)}`],
    ];

    // Combinar todas las filas (SIN incluir los totales)
    const maxRows = Math.max(
      ingresos.length,
      descuentos.length,
      aportaciones.length
    );
    for (let i = 0; i < maxRows; i++) {
      const fila = [
        ingresos[i] ? ingresos[i][0] : "",
        ingresos[i] ? ingresos[i][1] : "",
        descuentos[i] ? descuentos[i][0] : "",
        descuentos[i] ? descuentos[i][1] : "",
        aportaciones[i] ? aportaciones[i][0] : "",
        aportaciones[i] ? aportaciones[i][1] : "",
      ];
      tableData.push(fila);
    }

    autoTable(docPDF, {
      startY: docPDF.lastAutoTable.finalY,
      head: [
        ["INGRESOS", "VALOR", "DESCUENTOS", "VALOR", "APORTACIONES", "VALOR"],
      ],
      body: tableData,
      theme: "striped",
      styles: {
        fontSize: 7,
        halign: "left",
      },
      headStyles: {
        fillColor: [139, 69, 19], // Color marrón
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      tableWidth: 'auto',
      columnStyles: {
        0: { halign: "left", cellWidth: '16.666%' }, // Etiquetas ingresos - 16.666% (33.33% total para categoría)
        1: { halign: "right", cellWidth: '16.667%' }, // Valores ingresos - 16.667% (33.33% total para categoría)
        2: { halign: "left", cellWidth: '16.666%' }, // Etiquetas descuentos - 16.666% (33.33% total para categoría)
        3: { halign: "right", cellWidth: '16.667%' }, // Valores descuentos - 16.667% (33.33% total para categoría)
        4: { halign: "left", cellWidth: '16.666%' }, // Etiquetas aportaciones - 16.666% (33.33% total para categoría)
        5: { halign: "right", cellWidth: '16.667%' }, // Valores aportaciones - 16.667% (33.33% total para categoría)
      },
    });

    // Crear tabla separada para los totales (sin header)
    const totalesData = [
      [
        "TOTAL INGRESOS",
        `S/ ${(boleta.total_ingresos_bruto || 0).toFixed(2)}`,
        "TOTAL DESCUENTOS",
        `S/ ${(boleta.total_descuentos || 0).toFixed(2)}`,
        "TOTAL APORTACIONES",
        `S/ ${(boleta.total_aportaciones || 0).toFixed(2)}`,
      ],
    ];

    autoTable(docPDF, {
      startY: docPDF.lastAutoTable.finalY,
      head: [], // Sin header
      body: totalesData,
      theme: "striped",
      styles: {
        fontSize: 7,
        halign: "left",
      },
      tableWidth: 'auto',
      columnStyles: {
        0: { halign: "left", cellWidth: '16.666%' }, // Etiquetas ingresos - 16.666% (33.33% total para categoría)
        1: { halign: "right", fontStyle: "bold", cellWidth: '16.667%' }, // Valores ingresos en negrita - 16.667% (33.33% total para categoría)
        2: { halign: "left", cellWidth: '16.666%' }, // Etiquetas descuentos - 16.666% (33.33% total para categoría)
        3: { halign: "right", fontStyle: "bold", cellWidth: '16.667%' }, // Valores descuentos en negrita - 16.667% (33.33% total para categoría)
        4: { halign: "left", cellWidth: '16.666%' }, // Etiquetas aportaciones - 16.666% (33.33% total para categoría)
        5: { halign: "right", fontStyle: "bold", cellWidth: '16.667%' }, // Valores aportaciones en negrita - 16.667% (33.33% total para categoría)
      },
      didParseCell: function (data) {
        // Poner en negrita las etiquetas de totales
        if (
          data.cell.text[0] &&
          (data.cell.text[0].includes("TOTAL INGRESOS") ||
            data.cell.text[0].includes("TOTAL DESCUENTOS") ||
            data.cell.text[0].includes("TOTAL APORTACIONES"))
        ) {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    // Agregar fila en blanco antes del Neto a Pagar
    const netoData = [
      ["", "", "", "", "", ""],
      [
        `NETO A PAGAR: S/ ${(boleta.neto_pagar || 0).toFixed(2)}`,
        "",
        "",
        "",
        "",
        "",
      ],
    ];

    autoTable(docPDF, {
      startY: docPDF.lastAutoTable.finalY,
      head: [], // Sin header
      body: netoData,
      theme: "striped",
      styles: {
        fontSize: 7,
        halign: "left",
      },
      tableWidth: 'auto',
      columnStyles: {
        0: { cellWidth: '16.666%' },
        1: { cellWidth: '16.667%' },
        2: { cellWidth: '16.666%' },
        3: { cellWidth: '16.667%' },
        4: { cellWidth: '16.666%' },
        5: { cellWidth: '16.667%' },
      },
      didParseCell: function (data) {
        // Neto a pagar (segunda fila) - FUSIONAR COLUMNAS Y CENTRAR
        if (data.row.index === 1) {
          if (data.column.index === 0) {
            // Solo aplicar estilo a la primera columna
            data.cell.colSpan = 6; // Fusionar las 6 columnas
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fontSize = 10; // Aumentar tamaño de fuente
            data.cell.styles.fillColor = [240, 240, 240]; // Fondo gris claro
            data.cell.styles.halign = "center"; // Centrar texto
            data.cell.styles.textColor = [0, 0, 0]; // Texto negro
          } else {
            // Ocultar las demás celdas de la fila
            data.cell.text = [];
          }
        }
      },
    }); const finalY = docPDF.lastAutoTable.finalY + 2; // Margen reducido para el QR

    // Espacio adicional para las firmas
    const firmaY = finalY + 20; // Ajustar según espacio necesario

    console.log("Datos de empresa para firma:", empresaDatos); // Debug
    console.log("URL de firma:", empresaDatos?.firmaGG); // Debug

    // Verificar si necesitamos una nueva página
    if (firmaY > 250) {
      docPDF.addPage();
      const baseY = 30; // zona superior para firmas en nueva página
      docPDF.setFontSize(10);

      // Coordenadas comunes
      const topY = baseY + 5;

      // Intentar cargar firma del empleador si existe
      let sigHeight = 0;
      let sigWidth = 0;
      let firmaBase64 = null;
      if (empresaDatos?.firmaGG) {
        try {
          const res = await getBase64FromUrl(empresaDatos.firmaGG);
          firmaBase64 = res.base64;
          sigWidth = 60;
          sigHeight = Math.max(10, sigWidth / (res.width / res.height));
          if (sigHeight > 25) {
            sigHeight = 25;
            sigWidth = sigHeight * (res.width / res.height);
          }
        } catch (err) {
          console.error("Error cargando firma (nueva pág):", err);
          firmaBase64 = null;
        }
      }

      // espacio mínimo entre topY y la etiqueta de firma
      const spaceBelow = Math.max(sigHeight + 5, 20);
      const labelY = topY + spaceBelow;
      const lineY = labelY - 5; // línea de firma ligeramente arriba de la etiqueta

      // Siempre dibujar línea para empleador
      docPDF.text("____________________", 40, lineY);
      // Si hay imagen, dibujarla encima de la línea
      if (firmaBase64) {
        docPDF.addImage(firmaBase64, "JPEG", 40, topY, sigWidth, sigHeight);
      }
      docPDF.text("FIRMA DEL EMPLEADOR", 40, labelY);

      // Firma del empleado (derecha) - usar misma referencia vertical
      docPDF.text("___________________", 130, lineY);
      docPDF.text("FIRMA DEL EMPLEADO", 130, labelY);
    } else {
      // Firmas en la misma página (misma lógica pero usando firmaY como base)
      docPDF.setFontSize(10);
      const topY = firmaY + 5;

      let sigHeight = 0;
      let sigWidth = 0;
      let firmaBase64 = null;
      if (empresaDatos?.firmaGG) {
        try {
          const res = await getBase64FromUrl(empresaDatos.firmaGG);
          firmaBase64 = res.base64;
          sigWidth = 60;
          sigHeight = Math.max(10, sigWidth / (res.width / res.height));
          if (sigHeight > 25) {
            sigHeight = 25;
            sigWidth = sigHeight * (res.width / res.height);
          }
        } catch (err) {
          console.error("Error cargando firma (misma pág):", err);
          firmaBase64 = null;
        }
      }

      const spaceBelow = Math.max(sigHeight + 5, 20);
      const labelY = topY + spaceBelow;
      const lineY = labelY - 5;

      // Siempre dibujar línea para empleador
      docPDF.text("____________________", 40, lineY);
      // Si hay imagen, dibujarla encima de la línea
      if (firmaBase64) {
        docPDF.addImage(firmaBase64, "JPEG", 40, topY, sigWidth, sigHeight);
      }
      docPDF.text("FIRMA DEL EMPLEADOR", 40, labelY);

      docPDF.text("___________________", 130, lineY);
      docPDF.text("FIRMA DEL EMPLEADO", 130, labelY);
    }

    // Agregar texto en la esquina inferior derecha
    docPDF.setFontSize(8);
    docPDF.setTextColor(128, 128, 128); // Color gris para discreción
    docPDF.text(
      "Mi Boleta www.miboleta.net.pe",
      docPDF.internal.pageSize.width - 10,
      docPDF.internal.pageSize.height - 10,
      { align: "right" }
    );

    // Guardar PDF
    if (autoGuardar) {
      const parts = boleta.periodo.split('-');
      // Si el formato es YYYY-MM
      if (parts.length === 2) {
        const [anio, mes] = parts;
        docPDF.save(`${boleta.empleado_id}-${mes}_${anio}.pdf`);
      } else {
        // Fallback por si acaso
        docPDF.save(`${boleta.empleado_id}-${boleta.periodo}.pdf`);
      }
    }
    return docPDF;
  };

  const descargarMasivo = async () => {
    if (boletas.length === 0) {
      mostrarError("No hay boletas para descargar.");
      return;
    }

    const zip = new JSZip();
    let procesados = 0;
    let errores = 0;

    // Obtener periodo para el nombre del ZIP
    const primerBoleta = boletas[0];
    const [anio, mes] = primerBoleta.periodo.split('-');
    const nombreZip = `Boletas_${getMesTexto(parseInt(mes))}_${anio}.zip`;

    Swal.fire({
      title: "Generando archivo ZIP...",
      html: `
        <div class="mb-2">Procesando boletas...</div>
        <div class="progress" style="height: 20px;">
          <div id="progress-bar" class="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
               role="progressbar" style="width: 0%" 
               aria-valuenow="0" aria-valuemin="0" aria-valuemax="100">0%</div>
        </div>
        <div id="progress-text" class="mt-2 small text-muted">Iniciando...</div>
      `,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        // Swal.showLoading(); // Opcional, si queremos el spinner estándar también
      },
    });

    try {
      const folderName = `Boletas ${getMesTexto(parseInt(mes))} ${anio}`;
      const folder = zip.folder(folderName);

      for (const boleta of boletas) {
        try {
          // Actualizar UI
          const porcentaje = Math.round(((procesados) / boletas.length) * 100);
          const progressBar = document.getElementById('progress-bar');
          const progressText = document.getElementById('progress-text');

          if (progressBar) {
            progressBar.style.width = `${porcentaje}%`;
            progressBar.innerHTML = `${porcentaje}%`;
          }
          if (progressText) {
            progressText.innerText = `Generando PDF de: ${boleta.nombres}`;
          }

          // Generar PDF sin guardar automáticamente
          const docPDF = await generarPDF(boleta, false);
          const pdfBlob = docPDF.output("blob");

          // Nombre del archivo dentro del ZIP (Formato: DNI-mes_año.pdf)
          const [anioDoc, mesDoc] = boleta.periodo.split('-');
          const nombreArchivo = `${boleta.empleado_id}-${mesDoc}_${anioDoc}.pdf`;
          folder.file(nombreArchivo, pdfBlob);

          procesados++;
        } catch (error) {
          console.error(`Error generando boleta para ${boleta.nombres}:`, error);
          errores++;
        }
      }

      // Finalizar barra al 100% antes de comprimir
      const progressBar = document.getElementById('progress-bar');
      const progressText = document.getElementById('progress-text');
      if (progressBar) {
        progressBar.style.width = "100%";
        progressBar.innerHTML = "100%";
        progressBar.classList.remove("bg-primary");
        progressBar.classList.add("bg-success");
      }
      if (progressText) {
        progressText.innerText = "Comprimiendo archivo ZIP...";
      }

      // Generar ZIP y descargar
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, nombreZip);

      Swal.fire({
        icon: errores === 0 ? "success" : "warning",
        title: "Descarga completada",
        text: `Se descargó el archivo ZIP con ${procesados} boletas.${errores > 0 ? ` Hubo ${errores} errores.` : ""}`,
        confirmButtonColor: "#8B4513",
      });

    } catch (error) {
      console.error("Error en descarga masiva:", error);
      Swal.fire({
        icon: "error",
        title: "Error en descarga masiva",
        text: "Ocurrió un error al generar el archivo ZIP.",
        confirmButtonColor: "#8B4513",
      });
    }
  };

  const enviarMasivo = async () => {
    if (boletas.length === 0) {
      mostrarError("No hay boletas para enviar.");
      return;
    }

    if (!company) {
      mostrarError("Falta información de la empresa.");
      return;
    }

    const { isConfirmed } = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Se generarán y enviarán ${boletas.length} boletas a los correos de los colaboradores.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#8B4513",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, enviar a todos",
      cancelButtonText: "Cancelar"
    });

    if (!isConfirmed) return;

    Swal.fire({
      title: "Enviando boletas...",
      html: "Iniciando proceso...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const resultadosReporte = [];
    let exitosos = 0;
    let fallidos = 0;

    for (let i = 0; i < boletas.length; i++) {
      const boleta = boletas[i];
      const porcentaje = Math.round(((i + 1) / boletas.length) * 100);
      Swal.getHtmlContainer().textContent = `Procesando ${i + 1} de ${boletas.length} (${porcentaje}%)`;

      try {
        // 1. Generar PDF
        const docPDF = await generarPDF(boleta, false); // false para no autoguardar
        const pdfBlob = docPDF.output("blob");

        // 2. Preparar metadatos
        const [ano, mes] = boleta.periodo.split("-");
        const metadata = {
          dni: boleta.empleado_id,
          ano: ano,
          mes: mes,
          tipo: "Boletas de pago",
          nombreArchivo: `${boleta.empleado_id}_${ano}_${mes}.pdf`,
          empresaData: company,
          semana: ""
        };

        // 3. Enviar
        const resultado = await procesarDocumentoIndividual(pdfBlob, metadata);

        let detalleEstado = "Subido correctamente";
        if (resultado.notificacion) {
          if (resultado.notificacion.notificado) {
            detalleEstado += " y notificado por email/app";
          } else if (resultado.notificacion.error) {
            detalleEstado += ` pero fallo notificación: ${resultado.notificacion.error}`;
          }
        }

        exitosos++;
        resultadosReporte.push({
          "DNI": boleta.empleado_id,
          "Colaborador": boleta.nombres,
          "Periodo": boleta.periodo,
          "Estado": "EXITOSO",
          "Detalle": detalleEstado,
          "Fecha Hora": new Date().toLocaleString()
        });

      } catch (error) {
        console.error(`Error enviando boleta para ${boleta.nombres}:`, error);
        fallidos++;
        resultadosReporte.push({
          "DNI": boleta.empleado_id,
          "Colaborador": boleta.nombres,
          "Periodo": boleta.periodo,
          "Estado": "ERROR",
          "Detalle": error.message || "Error desconocido",
          "Fecha Hora": new Date().toLocaleString()
        });
      }
    }

    // Generar Reporte Excel
    if (resultadosReporte.length > 0) {
      try {
        const { utils, writeFile } = await import('xlsx');
        const ws = utils.json_to_sheet(resultadosReporte);

        // Ajustar ancho de columnas
        const wscols = [
          { wch: 12 }, // DNI
          { wch: 35 }, // Colaborador
          { wch: 10 }, // Periodo
          { wch: 10 }, // Estado
          { wch: 50 }, // Detalle
          { wch: 20 }  // Fecha
        ];
        ws['!cols'] = wscols;

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Reporte Envío");
        writeFile(wb, `Reporte_Envio_Boletas_${new Date().toISOString().slice(0, 10)}.xlsx`);
      } catch (err) {
        console.error("Error generando Excel:", err);
      }
    }

    Swal.fire({
      icon: fallidos === 0 ? "success" : "warning",
      title: "Proceso finalizado",
      html: `
        <p>Se enviaron <b>${exitosos}</b> boletas correctamente.</p>
        ${fallidos > 0 ? `<p style="color:red"><b>${fallidos}</b> fallaron.</p>` : ""}
        <p><small>Se ha descargado un reporte detallado (Excel) con el resultado de cada envío.</small></p>
      `,
      confirmButtonColor: "#8B4513",
    });
  };

  const mostrarError = (mensaje) => {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: mensaje,
      confirmButtonColor: "#8B4513",
    });
  };

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    if (boletas.length === 0) {
      return {
        totalBoletas: 0,
        totalIngresos: 0,
        totalDescuentos: 0,
        totalNeto: 0,
      };
    }

    return {
      totalBoletas: boletas.length,
      totalIngresos: boletas.reduce(
        (sum, b) => sum + (b.total_ingresos_bruto || 0),
        0
      ),
      totalDescuentos: boletas.reduce(
        (sum, b) => sum + (b.total_descuentos || 0),
        0
      ),
      totalNeto: boletas.reduce((sum, b) => sum + (b.neto_pagar || 0), 0),
    };
  };

  const estadisticas = calcularEstadisticas();

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        }}
      >
        <div className="text-center">
          <Spinner
            animation="border"
            style={{ color: "#8B4513", width: "3rem", height: "3rem" }}
          />
          <p className="mt-3 mb-0" style={{ color: "#8B4513" }}>
            Cargando datos iniciales...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Indicador de carga para búsquedas */}
        {searchLoading && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1080 }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-white mt-3 mb-0">
                Buscando boletas de pago...
              </p>
            </div>
          </div>
        )}

        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaFileInvoiceDollar className="me-3" />
                Boletas de Pago
              </h2>
              <p className="mb-0 opacity-75">
                Genera y descarga las boletas de remuneraciones de tus empleados
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con información */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        color: "white",
                      }}
                    >
                      <FaFileInvoiceDollar size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">
                      Generación de Boletas de Pago
                    </h5>
                    <p className="text-muted mb-0 small">
                      Consulta, visualiza y descarga las boletas de pago
                      individuales o masivas
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <Badge
                        style={{
                          background:
                            "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                          fontSize: "0.9rem",
                          padding: "8px 16px",
                        }}
                      >
                        <FaUsers className="me-1" />
                        {empleados.length} Empleados
                      </Badge>
                      <Badge
                        style={{
                          background:
                            "linear-gradient(135deg, #B8860B 0%, #DAA520 100%)",
                          fontSize: "0.9rem",
                          padding: "8px 16px",
                        }}
                      >
                        <FaFileAlt className="me-1" />
                        {boletas.length} Boletas
                      </Badge>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Formulario de filtros */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center">
                  <FaFilter className="me-2" />
                  <h5 className="mb-0 fw-semibold">Filtros de Búsqueda</h5>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    buscarBoletas();
                  }}
                >
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label
                          className="fw-semibold"
                          style={{ color: "#8B4513" }}
                        >
                          <FaUsers className="me-1" />
                          Empleado
                        </Form.Label>
                        <Form.Select
                          value={filtros.empleado}
                          onChange={(e) =>
                            setFiltros((prev) => ({
                              ...prev,
                              empleado: e.target.value,
                            }))
                          }
                          style={{
                            borderRadius: "10px",
                            border: "2px solid #e9ecef",
                          }}
                        >
                          <option value="">Todos los empleados</option>
                          {empleados.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.nombres} {emp.apellidos}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label
                          className="fw-semibold"
                          style={{ color: "#8B4513" }}
                        >
                          <FaCalendarAlt className="me-1" />
                          Mes
                        </Form.Label>
                        <Form.Select
                          value={filtros.mes}
                          onChange={(e) =>
                            setFiltros((prev) => ({
                              ...prev,
                              mes: parseInt(e.target.value),
                            }))
                          }
                          style={{
                            borderRadius: "10px",
                            border: "2px solid #e9ecef",
                          }}
                        >
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {new Date(2000, i).toLocaleString("es", {
                                month: "long",
                              })}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label
                          className="fw-semibold"
                          style={{ color: "#8B4513" }}
                        >
                          <FaCalendarAlt className="me-1" />
                          Año
                        </Form.Label>
                        <Form.Select
                          value={filtros.anio}
                          onChange={(e) =>
                            setFiltros((prev) => ({
                              ...prev,
                              anio: parseInt(e.target.value),
                            }))
                          }
                          style={{
                            borderRadius: "10px",
                            border: "2px solid #e9ecef",
                          }}
                        >
                          {Array.from({ length: 5 }, (_, i) => {
                            const anio = new Date().getFullYear() - 2 + i;
                            return (
                              <option key={anio} value={anio}>
                                {anio}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col>
                      <div className="d-flex gap-2 justify-content-end">
                        <Button
                          type="submit"
                          disabled={searchLoading}
                          className="d-flex align-items-center gap-2"
                          style={{
                            borderRadius: "10px",
                            background:
                              "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                            border: "none",
                            padding: "10px 20px",
                          }}
                        >
                          <FaSearch size="16px" />
                          {searchLoading ? "Buscando..." : "Buscar Boletas"}
                        </Button>
                        {boletas.length > 0 && (
                          <Button
                            variant="outline-secondary"
                            onClick={descargarMasivo}
                            disabled={searchLoading}
                            className="d-flex align-items-center gap-2"
                            style={{
                              borderRadius: "10px",
                              borderColor: "#D2691E",
                              color: "#D2691E",
                              padding: "10px 20px",
                            }}
                          >
                            <FaDownload size="16px" />
                            Descargar Todas ({boletas.length})
                          </Button>
                        )}
                        {boletas.length > 0 && (
                          <Button
                            variant="primary" // Color primario (marrón en este tema)
                            onClick={enviarMasivo}
                            disabled={searchLoading}
                            className="d-flex align-items-center gap-2"
                            style={{
                              borderRadius: "10px",
                              padding: "10px 20px",
                              background: "linear-gradient(135deg, #2e7d32 0%, #43a047 100%)", // Verde para diferenciar
                              border: "none"
                            }}
                          >
                            <FaFileInvoiceDollar size={16} />
                            Enviar a Todos ({boletas.length})
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            {/* Estadísticas */}
            {boletas.length > 0 && (
              <Row className="mb-4">
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <BsFileEarmarkPdf size={32} className="mb-2" />
                      <h4 className="fw-bold">{estadisticas.totalBoletas}</h4>
                      <p className="mb-0">Boletas</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaMoneyBillWave size={32} className="mb-2" />
                      <h4 className="fw-bold">
                        S/ {estadisticas.totalIngresos.toFixed(2)}
                      </h4>
                      <p className="mb-0">Total Ingresos</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaCog size={32} className="mb-2" />
                      <h4 className="fw-bold">
                        S/ {estadisticas.totalDescuentos.toFixed(2)}
                      </h4>
                      <p className="mb-0">Total Descuentos</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <BsDownload size={32} className="mb-2" />
                      <h4 className="fw-bold">
                        S/ {estadisticas.totalNeto.toFixed(2)}
                      </h4>
                      <p className="mb-0">Total Neto</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Tabla de resultados */}
            {boletas.length > 0 && (
              <Card
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <FaFileInvoiceDollar className="me-2" />
                      <h5 className="mb-0 fw-semibold">Boletas Encontradas</h5>
                    </div>
                    <Badge
                      bg="light"
                      text="dark"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {boletas.length} boletas
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                    <Table
                      responsive
                      hover
                      className="mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            <FaUsers className="me-1" />
                            Empleado
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            DNI
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                            <FaCalendarAlt className="me-1" />
                            Período
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-end">
                            Sueldo Base
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-end">
                            Total Ingresos Bruto
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-end">
                            Total Descuentos
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-end">
                            <FaMoneyBillWave className="me-1" />
                            Neto a Pagar
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {boletas.map((boleta, index) => (
                          <tr
                            key={index}
                            style={{ borderBottom: "1px solid #f1f3f4" }}
                          >
                            <td className="py-3 px-3">
                              <div className="fw-bold text-dark">
                                {boleta.nombres}
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <code className="text-muted">
                                {boleta.empleado_id}
                              </code>
                            </td>
                            <td className="py-3 px-3">
                              <Badge
                                style={{
                                  background:
                                    "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                                  fontSize: "0.75rem",
                                }}
                                className="px-2 py-1"
                              >
                                {boleta.periodo}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-end">
                              <span className="text-muted">
                                S/ {boleta.sueldo_base?.toFixed(2) || "0.00"}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-end">
                              <span className="fw-bold text-success">
                                S/ {boleta.total_ingresos_bruto?.toFixed(2) || "0.00"}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-end">
                              <span className="text-danger">
                                S/{" "}
                                {boleta.total_descuentos?.toFixed(2) || "0.00"}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-end">
                              <span
                                className="fw-bold"
                                style={{ color: "#8B4513" }}
                              >
                                S/ {boleta.neto_pagar?.toFixed(2) || "0.00"}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>Descargar Boleta PDF</Tooltip>
                                }
                              >
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => generarPDF(boleta)}
                                  style={{
                                    borderRadius: "8px",
                                    borderColor: "#8B4513",
                                    color: "#8B4513",
                                    padding: "6px 12px",
                                  }}
                                >
                                  <BsFileEarmarkPdf size="16px" />
                                </Button>
                              </OverlayTrigger>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Mensaje cuando no hay resultados */}
            {boletas.length === 0 && !searchLoading && (
              <Card
                className="border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="text-center py-5">
                  <div
                    className="bg-light rounded-circle mx-auto mb-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaFileInvoiceDollar size={32} className="text-muted" />
                  </div>
                  <h5 className="text-muted mb-2">No se encontraron boletas</h5>
                  <p className="text-muted mb-3">
                    Selecciona los filtros de búsqueda y presiona "Buscar
                    Boletas" para ver los resultados.
                  </p>
                  <Button
                    variant="primary"
                    onClick={buscarBoletas}
                    className="d-flex align-items-center gap-2 mx-auto"
                    style={{
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                      border: "none",
                    }}
                  >
                    <FaSearch size="14px" />
                    Buscar Boletas
                  </Button>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default BoletaPago;
